import type { ThunkAction, ThunkDispatch } from 'redux-thunk';
import type { Action, Dispatch } from 'redux';

export interface PaleState {
    hotelResults: HotelResults;
    hotel: Hotel;
    results: Results;
    filters: Filters;
    appState: AppState;
}

export type HotelResults = {
    numberOfOffers: number;
    totalPages: number;
    currentPage: number;
    offers: HotelOffer[];
    sortOptions: string[];
    defaultSort: string;
    status: ApiActionStatus;
};

export type AppState = {
    filtersOpen: boolean;
};

export interface HotelOffer {
    offerId: string;
    supplier: string;
    originAirport: string;
    destinationAirport: string;
    duration: number;
    departureDate: string;
    departureDateArrival?: string;
    departureStopovers?: number;
    departureTravelDuration?: number;
    returnDate: string;
    returnDateArrival?: string;
    returnStopovers?: number;
    returnTravelDuration?: number;
    flightDetails?: FlightDetails;
    pricePerPerson: number;
    board?: string;
    roomDescription: string;
    deepLink: string;
}

export interface FlightDetails {
    outbound: Flight[];
    inbound: Flight[];
}

export interface Flight {
    airlineCode?: string;
    airline?: string;
    flightNo?: string;
    originAirport: string;
    destinationAirport: string;
    departureTime: string;
    arrivalTime: string;
    flightDuration: number;
}

export interface Hotel {
    id: string;
    name: string;
    tripAdvisorId: string;
    rating: number;
    country: string;
    region: string;
    destination: string;
    facilities: string[];
    activities: string[];
    distanceToBeach: number;
    distanceToCity: number;
    imagePaths: string[];
}

export interface Results {
    numberOfHotels: number;
    totalPages: number;
    currentPage: number;
    offers: Offer[];
    facets: Facets;
    sortOptions: string[];
    defaultSort: string;
    status: ApiActionStatus;
}

export interface Offer {
    offerId: string;
    supplier: string;
    country: string;
    region: string;
    destination: string;
    originAirportCode: string;
    destinationAirportCode: string;
    rating: number;
    board?: string;
    facilities: string[];
    activities: string[];
    distanceToBeach: number;
    distanceToCity: number;
    imagePaths: string[];
    hotelId: string;
    hotelName: string;
    hotelTripAdvisorId?: string;
    duration: number;
    departureDate: string;
    departureDateArrival?: string;
    departureStopovers?: number;
    returnDate: string;
    returnDateArrival?: string;
    returnDateStopovers?: number;
    pricePerPerson: number;
    deepLink: string;
}

export interface Facets {
    location: NestedFacet[];
    originAirport: Facet[];
    charterSuppliers: Facet[];
    nonCharterSuppliers: Facet[];
    activities: Facet[];
    facilities: Facet[];
    meals: Facet[];
}

export interface Facet {
    value: string;
    count?: number;
}

export interface NestedFacet {
    value: string;
    count?: number;
    children?: NestedFacet[];
}

export type CheckboxFilters = 'meals' | 'facilities' | 'activities' | 'suppliers' | 'rating';

export interface Filters {
    sort?: string;
    page?: string;
    origin: string;
    destinations: Destinations;
    meals: CheckboxMap;
    facilities: CheckboxMap;
    activities: CheckboxMap;
    priceRange?: NumberRange;
    keywords: string;
    suppliers: CheckboxMap;
    distanceToBeach?: NumberRange;
    distanceToCity?: NumberRange;
    rating: CheckboxMap;
    travelMonths: Month[];
    departureDate?: FlexibleDate;
    returnDate?: FlexibleDate;
    duration?: NumberRange;
    hotelId?: string;
    ignoredFilters: (keyof Filters)[];
}

export interface OfferAndFilters {
    offer: Offer;
    filters: Filters;
}

export interface HotelAndOffer {
    hotel: Hotel;
    offer: HotelOffer;
}

export interface Destinations {
    list: string[];
    tree: CheckboxTree;
}

export interface CheckboxMap {
    [key: string]: boolean;
}

export interface CheckboxTree {
    [key: string]: CheckboxTree;
}

export interface ApiQueryParams {
    sort?: string;
    page?: string;
    from?: string;
    to?: string[] | string;
    meals?: string[] | string;
    facilities?: string[] | string;
    activities?: string[] | string;
    price?: string;
    q?: string;
    suppliers?: string[] | string;
    beach?: string;
    city?: string;
    rating?: string[] | string;
    travelMonths?: string[] | string;
    duration?: string;
    departureDate?: string;
    returnDate?: string;
    hotelId?: string;
}

export interface OutPage {
    offerId: string;
    supplier: string;
    deepLink: string;
    originAirportCode: string;
    pricePerPerson: number;
    fromPage?: string;
    country: string;
    region: string;
    destination: string;
    departureDate: string;
    returnDate: string;
}

export type NumberRange = [number, number];

export interface FlexibleDate {
    date?: string;
    flexibility?: number;
}

export interface FlexibilityOption {
    value: number;
    text: string;
}

export type Month = {
    year: number;
    month: number;
};

export enum ApiActionStatus {
    NOT_STARTED = 'NOT_STARTED',
    PENDING = 'PENDING',
    ERROR = 'ERROR',
    OK = 'OK',
}

export type OverrideValues<K, T> = { [P in keyof K]: T };

export type ThunkResult<R> = ThunkAction<R, PaleState, undefined, Action>;
export type ThunkDispatchFunc = ThunkDispatch<PaleState, undefined, Action>;

// Use this if dispatch is needed as a prop in a component (instead of mapDispatchToProps)
export interface ThunkDispatchProp {
    dispatch: Dispatch<Action> & ThunkDispatchFunc;
}

export interface FetchrData {
    statusCode: number;
    data?: ApiQueryParams;
}

export interface Destination {
    id: number;
    city: string;
    country: string;
    description: string;
    locationCodes: string[];
    imageCode: string;
    facts: string;
    price: number;
}

export interface DestinationDetails {
    capital?: string;
    country?: string;
    language: string;
    currency: string;
    currencyConversion?: CurrencyConversion;
}

export interface CurrencyConversion {
    name: string;
    rate: number;
    unit: number;
}
